import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileSpreadsheet, Download, Loader2, Users, Building2, Info } from 'lucide-react';

// Modal do "Exportar p/ CNPJ Biz": coleta o e-mail do responsável (coluna
// obrigatória do modelo) e o termômetro, com um resumo do que vai no arquivo.
// Substitui o window.prompt anterior.

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Quantos leads e quantos com contato do dono serão exportados. */
    total: number;
    comDono: number;
    emailPadrao?: string;
    exportando?: boolean;
    onConfirm: (opts: { responsibleEmail: string; thermometer: number }) => void;
}

const ExportCnpjBizModal: React.FC<Props> = ({ isOpen, onClose, total, comDono, emailPadrao, exportando, onConfirm }) => {
    const [email, setEmail] = useState(emailPadrao || '');
    const [termo, setTermo] = useState(3);
    const emailValido = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[2100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-white/20 rounded-xl"><FileSpreadsheet size={18} /></div>
                        <div>
                            <h2 className="text-sm font-extrabold leading-tight">Exportar para o CNPJ Biz</h2>
                            <p className="text-[11px] text-white/85">Modelo de importação (.xlsx)</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20"><X size={18} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Resumo */}
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 font-bold text-slate-700 dark:text-slate-200">
                            <Building2 size={13} /> {total.toLocaleString('pt-BR')} empresa(s)
                        </span>
                        <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 font-bold text-amber-700 dark:text-amber-400">
                            <Users size={13} /> {comDono.toLocaleString('pt-BR')} com contato do dono
                        </span>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300 block mb-1.5">
                            E-mail do responsável na plataforma do CNPJ Biz <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && emailValido && !exportando) onConfirm({ responsibleEmail: email.trim(), thermometer: termo }); }}
                            autoFocus
                            placeholder="seuemail@dominio.com"
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                        />
                        <p className="flex items-start gap-1 text-[11px] text-slate-400 mt-1.5">
                            <Info size={12} className="shrink-0 mt-0.5" />
                            Coluna obrigatória do modelo — é a conta que vai ficar como dona dos leads no CNPJ Biz.
                        </p>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300 block mb-1.5">Termômetro (interesse)</label>
                        <div className="flex items-center gap-1.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button key={n} onClick={() => setTermo(n)}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${termo === n
                                        ? 'bg-emerald-500 text-white border-emerald-500'
                                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-emerald-300'}`}>
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-bold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                        Cancelar
                    </button>
                    <button
                        onClick={() => onConfirm({ responsibleEmail: email.trim(), thermometer: termo })}
                        disabled={!emailValido || !!exportando}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                    >
                        {exportando ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Baixar planilha
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ExportCnpjBizModal;
